import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Modal } from '../ui/Modal';
import { Button } from '../ui';
import { formatTime12 } from '../../utils/formatters';
import db from '../../lib/database';

// Changes-since-send panel (2026-07-29 dispatch-button reconciliation).
//
// Opens from the amber "↻ N changed" chip on a day-column header. Shows
// every office-originated edit made to that day's jobs AFTER its Daily
// Dispatch SMS went out (dispatch_change_log, sql/074), grouped by job,
// in plain words. Crews were already auto-notified by the push pipeline
// — this panel is for the DISPATCHER: know the texted lineup is stale,
// then either resend the whole day, text one job's crew, or dismiss a
// group as handled ("called them about it").

const FIELD_LABEL = {
  crew_ids: 'Crew',
  truck_ids: 'Trucks',
  leader_id: 'Lead',
  move_date: 'Date',
  time: 'Time',
  status: 'Status',
};

export default function DispatchChangesModal({ dateStr, jobs, clients, crew, trucks, onClose, onResend, onTextJob, onChanged }) {
  const [rows, setRows] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [staffByUserId, setStaffByUserId] = useState({});
  const [busyJob, setBusyJob] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [changes, staff] = await Promise.all([
          db.getDispatchChanges(dateStr),
          db.getUserProfiles().catch(() => []),
        ]);
        if (!alive) return;
        setRows(changes);
        const map = {};
        for (const s of staff) { if (s.user_id) map[s.user_id] = s.display_name; }
        setStaffByUserId(map);
      } catch (e) {
        if (alive) { setError(e.message); setRows([]); }
      }
    })();
    return () => { alive = false; };
  }, [dateStr]);

  const crewName = (id) => crew.find(c => c.id === id)?.name || 'Unknown crew';
  const truckName = (id) => trucks.find(t => t.id === id)?.name || 'Unknown truck';

  // One plain-words line per changed field.
  const describeField = (field, change) => {
    const { old: oldVal, new: newVal } = change || {};
    switch (field) {
      case '_created':
        return 'Job added to this day after the dispatch went out';
      case 'crew_ids': {
        const oldIds = oldVal || [], newIds = newVal || [];
        const added = newIds.filter(id => !oldIds.includes(id)).map(crewName);
        const removed = oldIds.filter(id => !newIds.includes(id)).map(crewName);
        const parts = [];
        if (added.length) parts.push('+ ' + added.join(', '));
        if (removed.length) parts.push('− ' + removed.join(', '));
        return `Crew: ${parts.join(' · ') || 'reordered'}`;
      }
      case 'truck_ids': {
        const oldNames = (oldVal || []).map(truckName);
        const newNames = (newVal || []).map(truckName);
        return `Trucks: ${oldNames.join(' + ') || 'none'} → ${newNames.join(' + ') || 'none'}`;
      }
      case 'leader_id':
        return `Lead: ${oldVal ? crewName(oldVal) : 'none'} → ${newVal ? crewName(newVal) : 'none'}`;
      case 'move_date': {
        const fmt = (v) => v ? format(parseISO(v), 'EEE, MMM d') : 'no date';
        return `Date: ${fmt(oldVal)} → ${fmt(newVal)}`;
      }
      case 'time': {
        const fmt = (v) => v ? formatTime12(v) : 'TBD';
        return `Time: ${fmt(oldVal)} → ${fmt(newVal)}`;
      }
      case 'status': {
        const fmt = (v) => (v || '—').replace(/_/g, ' ');
        return `Status: ${fmt(oldVal)} → ${fmt(newVal)}`;
      }
      default:
        return `${FIELD_LABEL[field] || field} changed`;
    }
  };

  // Group open rows by job. A job that got edited three times shows one
  // card with three timestamped entries, oldest first.
  const groups = [];
  if (rows) {
    const byJob = new Map();
    for (const r of rows) {
      if (!byJob.has(r.job_id)) byJob.set(r.job_id, []);
      byJob.get(r.job_id).push(r);
    }
    for (const [jobId, jobRows] of byJob) {
      const job = jobs.find(j => j.id === jobId) || null;
      const client = job ? clients.find(c => c.id === job.client_id) : null;
      groups.push({ jobId, job, client, rows: jobRows });
    }
  }

  const dismissGroup = async (jobId) => {
    setBusyJob(jobId);
    try {
      await db.clearDispatchChanges({ date: dateStr, jobId, reason: 'dismissed' });
      setRows(prev => (prev || []).filter(r => r.job_id !== jobId));
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyJob(null);
    }
  };

  const displayDate = format(parseISO(dateStr), 'EEEE, MMM d');

  return (
    <Modal title={`Changes Since Send — ${displayDate}`} onClose={onClose} width="max-w-2xl">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          These edits happened <span className="font-semibold">after</span> this day's dispatch was sent. Crews were auto-notified of each one, but the lineup they were texted is out of date. Resend the day, text one job's crew, or dismiss what you've handled.
        </p>

        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">{error}</div>}

        {rows === null && <div className="text-center py-8 text-slate-400 text-sm">Loading changes…</div>}

        {rows !== null && groups.length === 0 && !error && (
          <div className="text-center py-8 text-slate-400 text-sm">All caught up — no open changes for this day.</div>
        )}

        {groups.map(g => (
          <div key={g.jobId} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-900 truncate">
                  {g.client?.name || 'Unknown client'}
                  {g.job?.job_label ? <span className="text-slate-500 font-normal"> · {g.job.job_label}</span> : null}
                  {!g.job && <span className="text-slate-500 font-normal"> · job no longer on the board</span>}
                </div>
                <ul className="mt-1.5 space-y-1">
                  {g.rows.map(r => (
                    <li key={r.id} className="text-sm text-slate-700">
                      {Object.entries(r.details || {}).map(([field, change]) => (
                        <div key={field}>{describeField(field, change)}</div>
                      ))}
                      <div className="text-[11px] text-slate-400">
                        {format(new Date(r.changed_at), 'h:mm a')}
                        {staffByUserId[r.changed_by] ? ` · ${staffByUserId[r.changed_by]}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                {g.job && (
                  <Button variant="success" size="sm" onClick={() => onTextJob(g.job)}>
                    Text this crew
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  loading={busyJob === g.jobId}
                  onClick={() => dismissGroup(g.jobId)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        ))}

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={() => onResend(dateStr)}>
            📋 Resend Daily Dispatch
          </Button>
        </div>
      </div>
    </Modal>
  );
}
